import { CabAvatar, CabButton, CabExecPicker, CabIcon, CabPanel, CabTimezoneInput } from "@CabComponents";
import { Box, Chip, SxProps, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { PollTable, PollTableLegend } from "../../components/Meeting/GroupScheduling";
import CalendarSchedulerContainer from "../../components/Schedule/CalendarScheduler";
import {
  Calendar, Leader, MeetingPollPriority, Meetings, MeetingSlot, ParticipantsWithSelections, 
  SelectedSlots,
} from "../../store";
import { allTimeZones, colorDisplayMeetingSlots, TimeZone } from "../../utils/scheduleUtils";
import { Views as RBCViews } from 'react-big-calendar';
import classes from '../Schedule/Schedule.module.css';
import { getLeaderIconSrc } from "../../utils/leaderUtils";
import { IoLogInOutline, IoPeopleOutline, IoTimeOutline } from "react-icons/io5";
import colors from "../../colors";
import { SchedulingPreferences } from "../../store/cabinetApi/generated/scheduling";
import { uniqBy } from "lodash-es";

const calendarPanelWidth = 425;

export type PollSelectionTableProps = {
  attendeesDisplayName: string;
  handleCalendarTimezoneSelected: (timezone: TimeZone) => void;
  // returningUserEmailHashes: {[hash: string]: string};
  activeParticipants: {
    email: string; emailHash: string; name: string; required?: boolean; leaderId?: number;
  }[];
  participants: ParticipantsWithSelections;
  convertedSlots: ConvertedSlot[];
  handleCheckSlot: (email: string, value: number, priority: MeetingPollPriority | null) => void;
  selectedSlots: { [email: string]: Map<number, SelectedSlots[number] | null> };
  onOpenSlotNoteModal?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlots[number];
    mode: "add" | "edit";
  }) => void;
  onRemoveSlotNote?: (payload: {
    participantEmail: string;
    slotIndex: number;
    slot: SelectedSlots[number];
  }) => void;
  calendarTimezoneSelected?: TimeZone;
  isMobile?: boolean;
  name: string;
  description: string;
  durationText: string;
  participantLength: number;
  date?: { start: DateTime, end: DateTime };
  onDateChange?: (start: DateTime, end: DateTime) => void;
  userData?: {
    leaders: Leader[];
    meetingSlots: MeetingSlot[];
    recurringSlots: MeetingSlot[];
    meetings: Meetings;
    calendars: Calendar[];
    schedulingPrefs?: SchedulingPreferences;
    selectedLeader: Leader | null,
    onSelectedLeaderChange: (l: Leader | null) => void,
  };
  openCalendar: boolean;
  onCloseCalendar: () => void;
  enhancePollTableCursors: boolean;
  onEndReached: (value: boolean) => void;
  onFormStateChange?: (isValid: boolean, submit: () => void) => void;
  onSubmit: () => void;
  sx?: SxProps;
  showCalendarButton?: boolean;
  onViewCalendar?: () => void;
  isAuthenticated?: boolean;
  
  // Event fetching parameters
  googleCalIds: string[];
  msCalIds: string[];
  startDate: DateTime;
  endDate: DateTime;
  forTimezone: string;
  showCanceledDeclined?: boolean;
  prefetchNextPeriod?: boolean;
  allDaySlots?: boolean;
};

type ConvertedSlot = {
  converted: {
    start: DateTime;
    end: DateTime;
  };
  index: number;
  start: DateTime;
  end: DateTime;
};

const PollSelectionTable = ({
  attendeesDisplayName, handleCalendarTimezoneSelected,
  /*returningUserEmailHashes*/ activeParticipants, participants, convertedSlots, isMobile,
  handleCheckSlot, selectedSlots, onOpenSlotNoteModal, onRemoveSlotNote, calendarTimezoneSelected,
  name, durationText, participantLength, description, date, onDateChange, userData, openCalendar, onCloseCalendar,
  enhancePollTableCursors, onEndReached, onFormStateChange, onSubmit, sx,
  showCalendarButton, onViewCalendar, isAuthenticated,
  googleCalIds, msCalIds, startDate, endDate, forTimezone, showCanceledDeclined = true, prefetchNextPeriod = false,
  allDaySlots = false,
}: PollSelectionTableProps) => {
  const [showDescription, setShowDescription] = useState(false);

  const cabExecOptions = useMemo(() => (userData?.leaders || []).map(leader => {
    return {
      value: leader.id,
      label: `${leader.first_name} ${leader.last_name}`,
      icon: <CabAvatar
        src={getLeaderIconSrc(leader)}
        color={leader.color}
        name={`${leader.first_name}
        ${leader.last_name}`}
        size="small"
      />
    };
  }), [userData?.leaders]);

  const selectedLeaders = userData?.selectedLeader ? [userData.selectedLeader] : [];
  
  useEffect(() => {
    onFormStateChange?.(true, onSubmit);
  }, [onFormStateChange, onSubmit]);

  useEffect(() => {
    if (selectedLeaders.length === 0 && userData) {
      const participantEmails = activeParticipants.map(ap => ap.email);
      const activeLeaderIds = activeParticipants.map(ap => ap.leaderId).filter(Boolean);
      const matchingLeaders = userData.leaders.filter(l => (
        activeLeaderIds.includes(l.id) || participantEmails.includes(l.email)
      )) || [];
      if (matchingLeaders.length > 0) {
        userData.onSelectedLeaderChange(matchingLeaders[0]);
      }
    }
  }, [activeParticipants, selectedLeaders.length, userData]);

  const newMeeting = {
    id: -1,
    title: 'Proposed Slot',
    leader_info: selectedLeaders.map(l => ({ ...l, meeting_leader_id: -1 })),
  };

  const meetingSlots = userData ? colorDisplayMeetingSlots(
    userData.meetingSlots,
    selectedLeaders.map(l => l.id),
    userData?.meetings,
    undefined,
    classes.pattern,
    userData.schedulingPrefs,
    false,
    true,
  ) : [];

  let availableSlots: MeetingSlot[] = Object.values(selectedSlots).flatMap(p => [...p.values()])
    .filter((s) => !!s && s.priority === MeetingPollPriority.AVAILABLE)
    // NOTE: Get's us around an inconveince of a nullable s which we check for in the filter
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((s: any) => ({
      id: s.id,
      start_date: s.start.toISO(),
      end_date: s.end.toISO(),
      meeting: -1,
      is_exclude: false,
      editable: false,
    }));

  availableSlots = userData ? colorDisplayMeetingSlots(
    availableSlots,
    selectedLeaders.map(l => l.id),
    { [-1]: newMeeting } as unknown as Meetings,
    -1,
    classes.pattern,
    userData.schedulingPrefs,
  ) : [];

  let potentialSlots: MeetingSlot[] = Object.values(selectedSlots).flatMap(p => [...p.values()])
    .filter((s) => !!s && s.priority === MeetingPollPriority.POTENTIAL)
    // NOTE: Get's us around an inconveince of a nullable s which we check for in the filer
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((s: any) => ({
      id: s.id,
      start_date: s.start.toISO(),
      end_date: s.end.toISO(),
      meeting: -1,
      is_exclude: false,
      editable: false,
    }));

  potentialSlots = userData ? colorDisplayMeetingSlots(
    potentialSlots,
    selectedLeaders.map(l => l.id),
    { [-1]: newMeeting } as unknown as Meetings,
    -1,
    classes.pattern,
    userData.schedulingPrefs,
    true,
  ) : [];

  const allMeetingSlots = [...meetingSlots, ...availableSlots, ...potentialSlots].map(s => ({ ...s, editable: false }));

  const handleLeaderChange = (leaderId: number) => {
    if (userData) {
      const leader = userData.leaders.find(l => l.id === leaderId);
      userData.onSelectedLeaderChange(leader || null);
    }
  };

  const handleDateChange = (curDate: DateTime) => {
    if (!onDateChange) return;

    const newDate = calendarTimezoneSelected ? curDate.setZone(calendarTimezoneSelected.name) : curDate;
    const newDateStart = newDate.startOf('day');
    const newDateEnd = newDate.endOf('day');
    onDateChange(newDateStart, newDateEnd);
  };


  const allowedDates = uniqBy(convertedSlots.map(s => s.converted.start.startOf('day')), s => s.toMillis());
  
  return (
    <Box
      id="poll-selection-table"
      sx={{
        display: "flex",
        ...sx,
        width: '100%',
        flexDirection: { xs: 'column', lg: 'row' },
        position: 'relative'
      }}>
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          overflow: 'visible',
          marginRight: {
            lg: openCalendar ? 0 : `-${calendarPanelWidth}px`,
          },
        }}
      >
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, md: 4 },
            width: '100%',
            overflow: 'visible',
          }}
        >
          <Box 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1, md: 2 },
              width: '100%',
            }}
          >
            {!isMobile ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  marginRight: 1
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: 32,
                      fontWeight: 300,
                      fontFamily: "sentient"
                    }}>
                    Choose preferred time slots
                  </Typography>
                  <Typography
                    variant="body2"
                    color={colors.calmGrey600}
                    sx={{
                      fontSize: 16,
                      maxWidth: '580px'
                    }}>
                    Indicate your availability (1 click = Available, 2 clicks = Potentially Available) and include a
                    note to provide additional context.
                  </Typography>
                </Box>
                {showCalendarButton && (
                  <CabButton
                    onClick={onViewCalendar}
                    icon={<CabIcon Icon={IoLogInOutline} />}
                    buttonType="tertiary"
                    color="accent"
                    size="small"
                    sx={{height: 30, marginLeft: 1, display: { xs: 'none', lg: 'inline-flex' }}}
                  >
                    {isAuthenticated ? 'View Executive Calendar' : 'Log in to View Executive Calendar'}
                  </CabButton>
                )}
              </Box>
            ) : (
              <Box sx={{
                marginBottom: 1
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    marginTop: 1
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2
                    }}>
                    <Typography variant="h1">{name}</Typography>
                    <Box
                      sx={{
                        gap: 1,
                        display: "flex",
                        alignItems: "center"
                      }}>
                      <Chip
                        sx={{ borderRadius: '6px', fontWeight: 500 }}
                        icon={<CabIcon Icon={IoTimeOutline} alt='respondents' sx={{fontSize: '16px'}}/>}
                        label={durationText}
                      />
                      <Chip
                        sx={{ borderRadius: '6px', fontWeight: 500 }}
                        icon={<CabIcon Icon={IoPeopleOutline} alt='respondents' sx={{fontSize: '16px'}}/>}
                        label={`${participantLength} responded`}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  {description && showDescription && (
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        marginTop: 2
                      }}>{description}</Typography>
                  )}
                  {description && (
                    <CabButton
                      buttonType='text'
                      onClick={() => setShowDescription(!showDescription)}
                      sx={{
                        fontSize: '14px',
                        minWidth: 'auto',
                        padding: 0,
                        paddingTop: 1,
                        '& .MuiButtonBase-root': {
                          padding: 0,
                        },
                        '& .button-text': {
                          fontSize: '14px',
                          textDecoration: 'underline !important'
                        },
                        textDecoration: 'underline'
                      }}
                    >
                      {showDescription ? 'Hide' : 'Show'} Description
                    </CabButton>
                  )}
                  <Typography
                    variant="body1"
                    sx={{
                      marginTop: 3,
                      fontWeight: 500
                    }}>
                    Indicate your availability (1 click = Available, 2 clicks = Potentially Available) and include a
                    note to provide additional context.
                  </Typography>
                </Box>
              </Box>
            )}
            <Box 
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                marginRight: 1,
                flexDirection: 'row',
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
            >
              <CabTimezoneInput
                shortFormat={isMobile}
                onChange={tzName => {
                  const tz = allTimeZones.find(zone => zone.name === tzName);
                  if (tz) {
                    handleCalendarTimezoneSelected(tz);
                  }
                }}
                value={calendarTimezoneSelected?.name || ''}
                sx={{ 
                  flex: isMobile ? 1 : 0,
                  minWidth: { xs: '80px', sm: '315px', md: '350px' },
                  width: { xs: '80px', sm: 'auto' }
                }}
              />
              <PollTableLegend hidePopular />
            </Box>
          </Box>
          <Box 
            sx={{
              width: '100%',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <PollTable
              activeParticipants={activeParticipants}
              votable={true}
              name={attendeesDisplayName}
              participants={participants}
              timezoneConvertedSlots={convertedSlots}
              onCheckSlot={handleCheckSlot}
              selectedSlots={selectedSlots}
              onOpenSlotNoteModal={onOpenSlotNoteModal}
              onRemoveSlotNote={onRemoveSlotNote}
              calendarOpen={openCalendar}
              calendarDate={date?.start}
              onEndReached={onEndReached}
              enhancePollTableCursors={enhancePollTableCursors}
              onDateChange={handleDateChange}
              sx={{ width: '100%' }}
              allDaySlots={allDaySlots}
            />
          </Box>
        </Box>
      </Box>
      {userData && date && onDateChange && (
        <CabPanel
          open={openCalendar}
          onClose={onCloseCalendar}
          closeIcon
          anchor="right"
          sx={{ zIndex: openCalendar ? 1200 : -1 }}
          width={calendarPanelWidth}
          title={(
            <CabExecPicker
              options={cabExecOptions}
              placeholder="Select a teammember"
              value={userData?.selectedLeader?.id || null}
              sx={{ fontSize: '16px', fontWeight: 700, height: '26px', marginBottom: 1, marginTop: 1,
                '& .MuiInputBase-input': {
                  lineHeight: '20px',
                  width: calendarPanelWidth - 90,
                }
              }}
              onChange={handleLeaderChange}
            />
          )}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              height: "100%"
            }}>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                flexDirection: "column"
              }}>
              <CalendarSchedulerContainer
                selectedLeaders={userData?.selectedLeader ? [userData.selectedLeader.id] : []}
                selectedSlots={allMeetingSlots}
                recurringSlots={userData.recurringSlots}
                hideTodayButton
                hideViewPicker
                noCalendarBorder
                hideResourceHeader
                hideTimezonePicker
                interactive={false}
                allowedDates={allowedDates}
                defaultCalendarView={RBCViews.DAY}
                // hideCalendarHeader={true}
                googleCalIds={googleCalIds}
                msCalIds={msCalIds}
                startDate={startDate}
                endDate={endDate}
                forTimezone={forTimezone}
                showCanceledDeclined={showCanceledDeclined}
                prefetchNextPeriod={prefetchNextPeriod}
                onEventsLoaded={() => {}}
                onLoadingChange={() => {}}
                onError={() => {}}
                onDateRangeChange={onDateChange}
              />
            </Box>
          </Box>
        </CabPanel>
      )}
    </Box>
  );
};

export default PollSelectionTable;
